/**
 * Trackside fans as TRACK ELEMENTS (M4): placed deliberately against a
 * ground-level straight, blades facing the channel, with a marble boost.
 * (They lived in the decor mid band before — flush against the track their
 * palette-tinted housings read as deck protrusions; deliberate placement +
 * orientation + the boost make them read as machinery instead.)
 */
import type { Dir, Placement } from "../shared/types.ts";
import type { Rng } from "./rng.ts";
import { DIR_STEP } from "./geom.ts";

const FAN_CHANCE = 0.35;
const BOOST = 6; // marble acceleration (units/s²) while in range

export interface FanPlan {
  placements: Placement[];
  cells: string[];
}

export function planFans(
  rng: Rng,
  /** All placements so far (straights located, cells avoided). */
  placements: readonly Placement[],
  occupied: ReadonlySet<string>,
): FanPlan {
  const empty: FanPlan = { placements: [], cells: [] };
  if (!rng.chance(FAN_CHANCE)) return empty;

  const key = (x: number, z: number) => `${x},${z}`;
  const arched = new Set(
    placements
      .filter((p) => p.pieceId === "tunnel")
      .map((p) => key(Math.round(p.pos[0]), Math.round(p.pos[2]))),
  );
  // Bridge decks stack over channels — a fan under a bridge clips the deck.
  const cellCounts = new Map<string, number>();
  for (const p of placements) {
    if (p.role !== "track") continue;
    const k = key(Math.round(p.pos[0]), Math.round(p.pos[2]));
    cellCounts.set(k, (cellCounts.get(k) ?? 0) + 1);
  }

  interface Candidate {
    cell: [number, number];
    /** Direction from the fan cell toward the track. */
    facing: Dir;
  }
  const taken = new Set<string>(occupied);
  const candidates: Candidate[] = [];
  for (const p of placements) {
    if (p.pieceId !== "straight" || p.role !== "track") continue;
    if (p.pos[1] !== 0) continue;
    const cx = Math.round(p.pos[0]);
    const cz = Math.round(p.pos[2]);
    if (arched.has(key(cx, cz))) continue;
    if ((cellCounts.get(key(cx, cz)) ?? 0) > 1) continue;
    const flow = ((1 - p.rotation + 4) % 4) as Dir;
    for (const side of [1, 3] as const) {
      const w = ((flow + side) % 4) as Dir;
      const [dx, dz] = DIR_STEP[w];
      const s1: [number, number] = [cx + dx, cz + dz];
      if (taken.has(key(...s1))) continue;
      candidates.push({ cell: s1, facing: ((w + 2) % 4) as Dir });
    }
  }
  if (candidates.length === 0) return empty;

  const count = rng.int(1, 3);
  const plan: FanPlan = { placements: [], cells: [] };
  for (const cand of rng.shuffle(candidates)) {
    if (plan.placements.length >= count) break;
    if (taken.has(key(...cand.cell))) continue;
    plan.placements.push({
      pieceId: rng.pick([
        "fan-standing-two",
        "fan-standing-four",
        "fan-two",
        "fan-four",
        "fan-simple-two",
        "fan-simple-four",
      ]),
      pos: [cand.cell[0], 0, cand.cell[1]],
      // Local +z (the blade side) faces the track.
      rotation: ((1 - cand.facing + 4) % 4) as Dir,
      role: "track",
      spin: { axis: [0, 0, 1], radPerSec: 4 + rng.float() * 3 },
      boost: BOOST,
    });
    taken.add(key(...cand.cell));
    plan.cells.push(key(...cand.cell));
  }
  return plan;
}
